<template>
  <form-layout v-if="selectedJobs.length == 0" title="Update Jobs" @on-dismiss="dismiss">
    <div class="form-spacing">
      <v-row>
        <v-col class="py-0">
          <vs-text type="subdued">Please choose at least one job to update </vs-text>
        </v-col>
      </v-row>
    </div>
  </form-layout>

  <form-layout v-else :title="startCase(updateJobsLabel)" @on-dismiss="dismiss">
    <div class="form-spacing">
      <v-row>
        <v-col class="py-0">
          <vs-text type="subdued">{{ formDescription }}</vs-text>
        </v-col>
      </v-row>

      <v-row v-if="!availableFields.length && !availableActions.length">
        <v-col class="pt-0">
          <vs-text type="subdued"
            >No bulk options available, try modifying your selection of jobs
          </vs-text>
        </v-col>
      </v-row>

      <template v-if="availableActions.length > 0">
        <v-row class="pt-3">
          <v-col>
            <vs-heading type="overline">Available Actions</vs-heading>
          </v-col>
        </v-row>

        <v-list>
          <div v-for="field in availableActions" :key="field.key">
            <v-card outlined rounded="lg" class="card-margin">
              <v-list-item :data-test="`bulk-update-${field.key}-option`" @click="field.onClick">
                <v-list-item-content>
                  <vs-text>{{ field.name }}</vs-text>
                </v-list-item-content>
              </v-list-item>
            </v-card>
            <promotion-callout-card
              v-if="showQboCallout && field.key === 'add-to-batch'"
              :key="`${field.key}-qbo-callout`"
              class="card-margin"
              title="Sync to QuickBooks Online"
              message="Automate your billing workflow"
              :url="qboAppSetupDeepLink"
              dismissible
              @dismiss="dismissQboBulkUpdateCallout"
            >
              <template #icon>
                <qbo-logo />
              </template>
            </promotion-callout-card>
          </div>
        </v-list>
      </template>

      <template v-if="availableFields.length > 0">
        <v-row class="pt-3">
          <v-col>
            <vs-heading type="overline">Available Fields</vs-heading>
          </v-col>
        </v-row>

        <v-list>
          <div v-for="field in availableFields" :key="field.key">
            <v-card :key="field.key" outlined rounded="lg" class="card-margin">
              <v-list-item :data-test="`bulk-update-${field.key}-option`" @click="field.onClick">
                <v-list-item-content>
                  <vs-text>{{ field.name }}</vs-text>
                </v-list-item-content>
              </v-list-item>
            </v-card>
          </div>
        </v-list>
      </template>

      <template v-if="unavailableOptions.length > 0">
        <v-row class="pt-3">
          <v-col class="shrink">
            <vs-heading type="overline">Unavailable</vs-heading>
          </v-col>
          <v-col class="pt-2 pl-1">
            <v-tooltip bottom max-width="380" open-delay="150">
              <template #activator="{ on }">
                <v-icon v-on="on">info</v-icon>
              </template>
              <span>
                Only simple custom fields that are common to all jobs can be bulk updated
              </span>
            </v-tooltip>
          </v-col>
        </v-row>

        <v-list>
          <div v-for="field in unavailableOptions" :key="field.key">
            <v-card :key="field.key" outlined rounded="lg" class="card-margin">
              <v-tooltip bottom open-delay="150">
                <template #activator="{ on }">
                  <div v-on="on">
                    <v-list-item disabled :data-test="`disabled-bulk-update-${field.key}-option`">
                      <v-list-item-content>
                        <vs-text :class="'grey--text text--lighten-1'">{{ field.name }} </vs-text>
                      </v-list-item-content>
                    </v-list-item>
                  </div>
                </template>
                <span style="white-space: pre">{{ field.disabledText }}</span>
              </v-tooltip>
            </v-card>
          </div>
        </v-list>
      </template>
    </div>

    <template #snack-bar-wrapper>
      <vs-snackbar :value="!!snackbarMessage" sticky type="info" @input="clearSnackbarMessage"
        >{{ snackbarMessage }}
      </vs-snackbar>
    </template>

    <template #bottom-sheet>
      <component
        :is="coreFieldFormComponent"
        v-if="coreFieldFormComponent"
        :button-label="updateJobsLabel"
        :selected-jobs="selectedJobs"
        @updated="handleUpdate"
        @dismiss="selectedForm = null"
      ></component>
      <component
        :is="customFieldFormComponent"
        v-if="customFieldFormComponent"
        :custom-field="selectedCustomField"
        :button-label="updateJobsLabel"
        :selected-jobs="selectedJobs"
        @updated="handleUpdate"
        @dismiss="selectedForm = null"
      ></component>
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import FormLayout from '../common/FormLayout.vue'
import BulkAssignForm from './BulkAssignForm.vue'
import BulkApproveForm from './BulkApproveForm.vue'
import BulkUnapproveForm from './BulkUnapproveForm.vue'
import BulkCancelForm from './BulkCancelForm.vue'
import BulkRejectForm from './BulkRejectForm.vue'
import BulkSubmitApprovalForm from './BulkSubmitApprovalForm.vue'
import BulkUnassignForm from './BulkUnassignForm.vue'
import BulkArchiveForm from './BulkArchiveForm.vue'
import BulkUpdateCostCenterForm from './BulkUpdateCostCenterForm.vue'
import BulkUpdateCustomFieldForm from './BulkUpdateCustomFieldForm.vue'
import BulkUpdateDestinationForm from './BulkUpdateDestinationForm.vue'
import BulkUpdateDetailsForm from './BulkUpdateDetailsForm.vue'
import BulkUpdatePickupForm from './BulkUpdatePickupForm.vue'
import BulkUpdatePriorityForm from './BulkUpdatePriorityForm.vue'
import BulkUpdateProjectForm from './BulkUpdateProjectForm.vue'
import BulkUpdateScheduleForm from './BulkUpdateScheduleForm.vue'
import BulkUpdateTagForm from '@/components/jobs/BulkUpdateTagForm.vue'
import BulkRemoveJobsFromBatchesForm from '@/components/jobs/BulkRemoveJobsFromBatchesForm.vue'
import { Component, computed, ref, watch } from 'vue'
import { sortBy, startCase } from 'lodash'
import { getBulkUpdatableFields, SimpleField } from '@/utils/field-schema-helper'
import Task from '@/models/task.js'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { useState, useStore } from 'vuex-composition-helpers'
import { ApprovalStatus, JobStatus } from '@/models/job'
import BulkAddJobsToBatchForm from '@/components/jobs/BulkAddJobsToBatchForm.vue'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'
import { usePromotionQuery } from '@/components/batches/Promotion/usePromotionQuery'
import QboLogo from '@/components/common/logos/QboLogo.vue'
import PromotionCalloutCard from '@/components/common/callouts/PromotionCalloutCard.vue'

const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', updatedJobs: JobJson[])
  (e: 'bottom-sheet-open')
  (e: 'bottom-sheet-closed')
}>()

type DisabledReasons = {
  approved?: boolean
  cancelled?: boolean
  completed?: boolean
  inProgressOrFinal?: boolean
  multipleHubs?: boolean
  multipleResourceTypes?: boolean
  noInventory?: boolean
  userNoPermission?: boolean
  noJobsHaveBatches?: boolean
  anyJobsNotApprovable?: boolean
  anyJobsNotUnapprovable?: boolean
  anyJobsNotRejectable?: boolean
  anyJobsNotSubmittableForApproval?: boolean
}

type OptionType = 'action' | 'field'

type Options = {
  key: string
  name: string
  disabledText: string
  disabled: boolean
  type: OptionType
  onClick: () => void
}

const props = withDefaults(
  defineProps<{
    selectedJobs: JobJson[]
    allowAddToProject?: boolean
    allowAddToBatch?: boolean
    allowRemoveFromBatches?: boolean
  }>(),
  {
    allowAddToProject: true,
    allowAddToBatch: true,
    allowRemoveFromBatches: true,
  },
)

const { currentOrgHasInvoicingEntitlement } = useBusinessUnitPermissions()
const {
  isQboBulkUpdatePromotionEnabled,
  isEntitlementsResolved,
  hasQboEntitlement,
  isQboBulkUpdateCalloutDismissed,
  dismissQboBulkUpdateCallout,
  qboAppSetupDeepLink,
} = usePromotionQuery()
const store = useStore()
const canViewAddToBatchSelectionPage = computed<boolean>(
  () => store.getters['permissions/canViewAddToBatchSelectionPage'],
)
const canEditBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canEditBatch'],
)
const canEditBatchesOfSelectedJobs = computed(() => {
  let result = true
  for (const job of props.selectedJobs) {
    if (!canEditBatch.value(job.assigned_service_provider_id)) result = false
  }
  return result
})
const invoicesFeatureFlag = computed<boolean>(() => store.getters['featureFlags/invoices'])
const canBulkUpdateJobs = computed<boolean>(() => store.getters['permissions/canBulkUpdateJobs'])
const canUnapproveJob = computed<
  (hubId: string, approvedBy: string | null, accountId: string) => boolean
>(() => store.getters['permissions/canUnapproveJob'])

const selectedForm = ref<
  | 'add-to-project'
  | 'add-to-batch'
  | 'remove-from-batches'
  | 'cancel'
  | 'cost-center'
  | 'destination'
  | 'details'
  | 'pickup'
  | 'priority'
  | 'schedule'
  | 'custom-field'
  | 'tags'
  | 'assign'
  | 'unassign'
  | 'archive'
  | 'approve'
  | 'unapprove'
  | 'reject'
  | 'submit-approval'
  | null
>()
const snackbarMessage = ref('')
const selectedCustomFieldKey = ref('')

const availableActions = computed<Options[]>(() => {
  const availableCoreActions = coreFieldOptions.value.filter(
    (option) => !option.disabled && option.type === 'action',
  )
  return sortBy(availableCoreActions, [(option) => option.name.toLowerCase()])
})

const availableFields = computed<Options[]>(() => {
  const availableCoreFields = coreFieldOptions.value.filter(
    (option) => !option.disabled && option.type === 'field',
  )
  const availableCustomOptions = customFieldOptions.value.filter((option) => !option.disabled)
  return sortBy(
    [...availableCoreFields, ...availableCustomOptions],
    [(option) => option.name.toLowerCase()],
  )
})

const hasAddToBatchOption = computed(() =>
  availableActions.value.some((option) => option.key === 'add-to-batch'),
)

const showQboCallout = computed(
  () =>
    isQboBulkUpdatePromotionEnabled.value &&
    hasAddToBatchOption.value &&
    isEntitlementsResolved.value &&
    !hasQboEntitlement.value &&
    !!qboAppSetupDeepLink.value &&
    !isQboBulkUpdateCalloutDismissed.value,
)

const unavailableOptions = computed<Options[]>(() => {
  const unavailableCoreOptions = coreFieldOptions.value.filter((option) => option.disabled)
  const unavailableCustomOptions = customFieldOptions.value.filter((option) => option.disabled)
  return sortBy(
    [...unavailableCoreOptions, ...unavailableCustomOptions],
    [(option) => option.name.toLowerCase()],
  )
})

const coreFieldOptions = computed<Options[]>(() => {
  const optionsArray: Options[] = [
    {
      key: 'assign',
      name: 'Assign',
      type: 'action',
      disabled:
        noJobsSelected.value ||
        anyJobsInProgressOrFinal.value ||
        jobsHaveDifferentResourceTypes.value ||
        jobsBelongToMultipleHubs.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('Editing job assignments unavailable:', {
        inProgressOrFinal: anyJobsInProgressOrFinal.value,
        multipleResourceTypes: jobsHaveDifferentResourceTypes.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'assign'),
    },
    {
      key: 'approve',
      name: 'Approve',
      type: 'action',
      disabled: noJobsSelected.value || anyJobsNotApprovable.value || !userCanApproveJob.value,
      disabledText: disabledToolTip('Approving jobs unavailable:', {
        anyJobsNotApprovable: anyJobsNotApprovable.value,
        userNoPermission: !userCanApproveJob.value,
      }),
      onClick: () => (selectedForm.value = 'approve'),
    },
    {
      key: 'unapprove',
      name: 'Unapprove',
      type: 'action',
      disabled: noJobsSelected.value || anyJobsNotUnapprovable.value,
      disabledText: disabledToolTip('Unapproving jobs unavailable:', {
        anyJobsNotUnapprovable: anyJobsNotUnapprovable.value,
      }),
      onClick: () => (selectedForm.value = 'unapprove'),
    },
    {
      key: 'reject',
      name: 'Reject Approval',
      type: 'action',
      disabled: noJobsSelected.value || anyJobsNotRejectable.value || !userCanApproveJob.value,
      disabledText: disabledToolTip('Rejecting job approvals unavailable:', {
        anyJobsNotRejectable: anyJobsNotRejectable.value,
        userNoPermission: !userCanApproveJob.value,
      }),
      onClick: () => (selectedForm.value = 'reject'),
    },
    {
      key: 'submit-approval',
      name: 'Submit for Approval',
      type: 'action',
      disabled:
        noJobsSelected.value ||
        anyJobsNotSubmittableForApproval.value ||
        !userCanSubmitJobForApproval.value,
      disabledText: disabledToolTip('Submitting jobs for approval unavailable:', {
        anyJobsNotSubmittableForApproval: anyJobsNotSubmittableForApproval.value,
        userNoPermission: !userCanSubmitJobForApproval.value,
      }),
      onClick: () => (selectedForm.value = 'submit-approval'),
    },
    {
      key: 'unassign',
      name: 'Unassign',
      type: 'action',
      disabled: noJobsSelected.value || anyJobsInProgressOrFinal.value || !userCanEditJob.value,
      disabledText: disabledToolTip('Editing job assignment unavailable:', {
        inProgressOrFinal: anyJobsInProgressOrFinal.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'unassign'),
    },
    {
      key: 'cost-center',
      name: 'Cost Center',
      type: 'field',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsApproved.value ||
        jobsBelongToMultipleHubs.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('Editing cost center unavailable:', {
        approved: anyJobsApproved.value,
        cancelled: anyJobsCancelled.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'cost-center'),
    },
    {
      key: 'destination',
      name: 'Destination',
      type: 'field',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsApproved.value ||
        jobsBelongToMultipleHubs.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('Editing destination unavailable:', {
        approved: anyJobsApproved.value,
        cancelled: anyJobsCancelled.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'destination'),
    },
    {
      key: 'details',
      name: 'Details',
      type: 'field',
      disabled:
        noJobsSelected.value ||
        anyJobsApproved.value ||
        anyJobsCancelled.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('Editing job details unavailable:', {
        approved: anyJobsApproved.value,
        cancelled: anyJobsCancelled.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'details'),
    },
    {
      key: 'cancel',
      name: 'Mark as Cancelled',
      type: 'action',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsCompleted.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('Cancelling jobs unavailable:', {
        completed: anyJobsCompleted.value,
        cancelled: anyJobsCancelled.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'cancel'),
    },
    {
      key: 'pickup',
      name: 'Pickup Location',
      type: 'field',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsApproved.value ||
        anyJobsHaveNoInventory.value ||
        jobsBelongToMultipleHubs.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('Editing pickup location unavailable:', {
        approved: anyJobsApproved.value,
        cancelled: anyJobsCancelled.value,
        noInventory: anyJobsHaveNoInventory.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'pickup'),
    },
    {
      key: 'priority',
      name: 'Priority',
      type: 'field',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsCompleted.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('Editing priority unavailable:', {
        completed: anyJobsCompleted.value,
        cancelled: anyJobsCancelled.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'priority'),
    },
    {
      key: 'schedule',
      name: 'Start Date/Time',
      type: 'field',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsCompleted.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('Editing start date/time unavailable:', {
        completed: anyJobsCompleted.value,
        cancelled: anyJobsCancelled.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'schedule'),
    },
    {
      key: 'tags',
      name: 'Tags',
      type: 'field',
      disabled: noJobsSelected.value || anyJobsCancelled.value || jobsBelongToMultipleHubs.value,
      disabledText: disabledToolTip('Editing tags unavailable:', {
        cancelled: anyJobsCancelled.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
      }),
      onClick: () => (selectedForm.value = 'tags'),
    },
    {
      key: 'archive',
      name: 'Archive',
      type: 'action',
      disabled: noJobsSelected.value || !userCanArchiveJob.value,
      disabledText: disabledToolTip('Archiving jobs unavailable:', {
        userNoPermission: !userCanArchiveJob.value,
      }),
      onClick: () => (selectedForm.value = 'archive'),
    },
  ]
  if (props.allowAddToProject) {
    optionsArray.unshift({
      key: 'add-to-project',
      name: 'Add to Project',
      type: 'action',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsApproved.value ||
        jobsBelongToMultipleHubs.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('Adding job to project unavailable:', {
        approved: anyJobsApproved.value,
        cancelled: anyJobsCancelled.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'add-to-project'),
    })
  }
  if (props.allowRemoveFromBatches && canEditBatchesOfSelectedJobs.value) {
    optionsArray.unshift({
      key: 'remove-from-batches',
      name: 'Remove from Batch',
      type: 'action',
      disabled: noJobsSelected.value || noJobsHaveBatches.value,
      disabledText: disabledToolTip('Removing jobs from batch unavailable:', {
        noJobsHaveBatches: noJobsHaveBatches.value,
      }),
      onClick: () => (selectedForm.value = 'remove-from-batches'),
    })
  }
  if (canViewAddToBatchSelectionPage.value && props.allowAddToBatch) {
    optionsArray.unshift({
      key: 'add-to-batch',
      name: 'Add to Batch',
      type: 'action',
      disabled: noJobsSelected.value || jobsBelongToMultipleHubs.value,
      disabledText: disabledToolTip('Adding jobs to batch unavailable:', {
        multipleHubs: jobsBelongToMultipleHubs.value,
      }),
      onClick: () => (selectedForm.value = 'add-to-batch'),
    })
  }
  return canBulkUpdateJobs.value
    ? optionsArray
    : optionsArray.filter((option) => option.key === 'approve' || option.key === 'unapprove')
})

const customFieldOptions = computed<Options[]>(() => {
  if (!canBulkUpdateJobs.value) return []

  return updatableCustomFields.value.map((customField) => ({
    key: customField.key,
    name: customField.definition.title,
    type: 'field',
    disabled:
      anyJobsApproved.value ||
      anyJobsCancelled.value ||
      jobsBelongToMultipleHubs.value ||
      !userCanEditJob.value,
    disabledText: disabledToolTip(customField.definition.title, {
      approved: anyJobsApproved.value,
      cancelled: anyJobsCancelled.value,
      multipleHubs: jobsBelongToMultipleHubs.value,
      userNoPermission: !userCanEditJob.value,
    }),
    onClick: () => handleCustomFieldClicked(customField.key),
  }))
})

const coreFieldFormComponent = computed<Component | null>(() => {
  if (selectedForm.value === 'add-to-project') {
    return BulkUpdateProjectForm
  } else if (canViewAddToBatchSelectionPage.value && selectedForm.value === 'add-to-batch') {
    return BulkAddJobsToBatchForm
  } else if (
    invoicesFeatureFlag.value &&
    currentOrgHasInvoicingEntitlement.value &&
    selectedForm.value === 'remove-from-batches'
  ) {
    return BulkRemoveJobsFromBatchesForm
  } else if (selectedForm.value === 'details') {
    return BulkUpdateDetailsForm
  } else if (selectedForm.value === 'priority') {
    return BulkUpdatePriorityForm
  } else if (selectedForm.value === 'schedule') {
    return BulkUpdateScheduleForm
  } else if (selectedForm.value === 'destination') {
    return BulkUpdateDestinationForm
  } else if (selectedForm.value === 'pickup') {
    return BulkUpdatePickupForm
  } else if (selectedForm.value === 'cost-center') {
    return BulkUpdateCostCenterForm
  } else if (selectedForm.value === 'cancel') {
    return BulkCancelForm
  } else if (selectedForm.value === 'tags') {
    return BulkUpdateTagForm
  } else if (selectedForm.value === 'assign') {
    return BulkAssignForm
  } else if (selectedForm.value === 'unassign') {
    return BulkUnassignForm
  } else if (selectedForm.value === 'archive') {
    return BulkArchiveForm
  } else if (selectedForm.value === 'approve') {
    return BulkApproveForm
  } else if (selectedForm.value === 'unapprove') {
    return BulkUnapproveForm
  } else if (selectedForm.value === 'reject') {
    return BulkRejectForm
  } else if (selectedForm.value === 'submit-approval') {
    return BulkSubmitApprovalForm
  }
  return null
})

const customFieldFormComponent = computed<Component | null>(() => {
  if (selectedForm.value === 'custom-field') {
    return BulkUpdateCustomFieldForm
  }

  return null
})

const updatableCustomFields = computed<SimpleField[]>(() => {
  const activityIds = props.selectedJobs.map((job) => job.activity_id)
  const { all: activities } = useState('tasks', ['all'])
  const activitySchemas = Object.values(activities.value)
    .filter((activity: Task) => activityIds.includes(activity.id))
    .map((activity: Task) => activity.jobCustomFieldsSchema)

  const { jobCustomFieldsSchema: referenceSchema } = useState('schemas', ['jobCustomFieldsSchema'])

  return getBulkUpdatableFields(activitySchemas, referenceSchema.value.schema.definitions)
})

const selectedCustomField = computed(() => {
  return updatableCustomFields.value.find((field) => field.key === selectedCustomFieldKey.value)
})

const handleCustomFieldClicked = (customFieldKey: string) => {
  selectedForm.value = 'custom-field'
  selectedCustomFieldKey.value = customFieldKey
}

const handleUpdate = ({ message, updatedJobs }: BulkUpdateResponse) => {
  closeForm()
  snackbarMessage.value = message
  emits('updated', updatedJobs)
}

watch(selectedForm, (selection) => {
  if (selection != null) {
    emits('bottom-sheet-open')
  } else {
    emits('bottom-sheet-closed')
  }
})

const closeForm = () => (selectedForm.value = null)
const clearSnackbarMessage = () => (snackbarMessage.value = '')
const dismiss = () => emits('dismiss')

const formDescription = computed(() => {
  const jobCount = props.selectedJobs.length
  return jobCount >= 2
    ? `Edit a field or apply an action for the ${jobCount} selected jobs`
    : 'Edit a field or apply an action for the selected job'
})

const updateJobsLabel = computed(() => {
  const jobCount = props.selectedJobs.length
  return jobCount >= 2 ? `Update ${jobCount} jobs` : 'Update job'
})

const anyJobsApproved = computed(() => {
  return props.selectedJobs.some((j) => j.approval_status === 'Approved')
})
const approvedJobCount = computed(() => {
  return props.selectedJobs.filter((j) => j.approval_status === 'Approved').length
})

const anyJobsCompleted = computed(() => {
  return props.selectedJobs.some((j) => j.status === 'Completed')
})
const completedJobCount = computed(() => {
  return props.selectedJobs.filter((j) => j.status === 'Completed').length
})

const anyJobsCancelled = computed(() => {
  return props.selectedJobs.some((j) => j.status === 'Cancelled')
})
const cancelledJobCount = computed(() => {
  return props.selectedJobs.filter((j) => j.status === 'Cancelled').length
})

const anyJobsHaveNoInventory = computed(() => {
  return props.selectedJobs.some((j) => j.inventory_requirements.length === 0)
})
const jobsWithNoInventoryCount = computed<number>(() => {
  return props.selectedJobs.filter((j) => j.inventory_requirements.length === 0).length
})

const jobsBelongToMultipleHubs = computed(() => {
  const hubs = new Set(props.selectedJobs.map((j) => j.hub_id))
  return [...hubs].length > 1
})

const noJobsHaveBatches = computed(() => props.selectedJobs.every((j) => !j.invoice_id))

const noJobsSelected = computed(() => props.selectedJobs.length === 0)

const anyJobsInProgressOrFinal = computed(() => {
  return props.selectedJobs.some(
    (j) =>
      j.status !== 'Requested' &&
      j.status !== 'AssignedToServiceProvider' &&
      j.status !== 'Scheduled' &&
      j.status !== 'AssignedToPool',
  )
})
const jobsInProgressOrFinalCount = computed(() => {
  return props.selectedJobs.filter(
    (j) =>
      j.status !== 'Requested' &&
      j.status !== 'AssignedToServiceProvider' &&
      j.status !== 'Scheduled' &&
      j.status !== 'AssignedToPool',
  ).length
})

const anyJobsNotApprovable = computed(() => {
  return props.selectedJobs.some(
    (j) => j.approval_status === 'Approved' || j.status !== 'Completed',
  )
})

const anyJobsNotUnapprovable = computed(() => {
  return props.selectedJobs.some(
    (job) =>
      job.approval_status !== 'Approved' ||
      job.is_archived ||
      !canUnapproveJob.value(job.hub_id, job.approved_by, job.account_id),
  )
})

const anyJobsNotRejectable = computed(() => {
  return props.selectedJobs.some((j) => j.approval_status !== 'PendingApproval')
})

const anyJobsNotSubmittableForApproval = computed(() => {
  return props.selectedJobs.some(
    (j) =>
      j.approval_status === 'Approved' ||
      j.approval_status === 'PendingApproval' ||
      j.status !== 'Completed',
  )
})

const jobsHaveDifferentResourceTypes = computed(() => {
  const selectedResourceTypes: Set<number> = new Set(
    props.selectedJobs.map((job) => job.resource_type_id),
  )
  return selectedResourceTypes.size > 1
})

const disabledToolTip = (message: string, disabledReasons: DisabledReasons): string => {
  if (noJobsSelected.value) {
    message += 'No jobs selected'
    return message
  }

  if (disabledReasons.approved) {
    const count = approvedJobCount.value
    message += `\n${count} ${count > 1 ? 'jobs are' : 'job is'} approved`
  }

  if (disabledReasons.inProgressOrFinal) {
    const count = jobsInProgressOrFinalCount.value
    message += `\n${count} ${count > 1 ? 'jobs are' : 'job is'} in progress or completed`
  }

  if (disabledReasons.completed) {
    const count = completedJobCount.value
    message += `\n${count} ${count > 1 ? 'jobs are' : 'job is'} completed`
  }

  if (disabledReasons.cancelled) {
    const count = cancelledJobCount.value
    message += `\n${count} ${count > 1 ? 'jobs are' : 'job is'} cancelled`
  }

  if (disabledReasons.noInventory) {
    const count = jobsWithNoInventoryCount.value
    message += `\n${count} ${count > 1 ? 'jobs have' : 'job has'} no inventory`
  }

  if (disabledReasons.multipleHubs) {
    message += '\nJobs belong to multiple hubs'
  }

  if (disabledReasons.multipleResourceTypes) {
    message += '\nJobs have multiple resource types'
  }

  if (disabledReasons.userNoPermission) {
    message += "\nYou don't have permission"
  }

  if (disabledReasons.noJobsHaveBatches) {
    message += '\nNo selected jobs belong to a batch'
  }

  if (disabledReasons.anyJobsNotApprovable) {
    message += '\nSome jobs are not eligible for approval'
  }

  if (disabledReasons.anyJobsNotRejectable) {
    message += '\nSome jobs are not eligible for rejection'
  }

  if (disabledReasons.anyJobsNotSubmittableForApproval) {
    message += '\nSome jobs are not eligible to be submitted for approval'
  }

  return message
}

const canModifyCompletedJobsPendingApproval = (hubId: string) =>
  store.getters['permissions/canModifyCompletedJobsPendingApproval'](hubId) as boolean
const canModifyCompletedJobsWithNoApproval = (hubId: string) =>
  store.getters['permissions/canModifyCompletedJobsWithNoApproval'](hubId) as boolean

const canArchiveJob = (hubId: string, accountId: string) =>
  store.getters['permissions/canArchiveJob'](hubId, accountId) as boolean

const canApproveJob = (hubId: string, accountId: string) =>
  store.getters['permissions/canApproveJob'](hubId, accountId) as boolean

const canSubmitJobForApproval = (serviceProviderId: string) =>
  store.getters['permissions/canSubmitForApproval'](serviceProviderId) as boolean

const userCanApproveJob = computed(() => {
  return props.selectedJobs.every((j) => canApproveJob(j.hub_id, j.account_id))
})

const userCanArchiveJob = computed(() => {
  return props.selectedJobs.every((j) => canArchiveJob(j.hub_id, j.account_id))
})

const userCanSubmitJobForApproval = computed(() => {
  return props.selectedJobs.every((j) =>
    canSubmitJobForApproval(j.assigned_service_provider_id || ''),
  )
})

const userCanEditJob = computed(() => {
  let result = true
  for (const job of props.selectedJobs) {
    if (job.status !== JobStatus.COMPLETED) {
      continue
    }
    if (job.approval_status === ApprovalStatus.PENDING_APPROVAL) {
      result &&= canModifyCompletedJobsPendingApproval(job.hub_id)
    } else if (job.approval_status === ApprovalStatus.NO_APPROVAL) {
      result &&= canModifyCompletedJobsWithNoApproval(job.hub_id)
    } else if (job.approval_status === ApprovalStatus.APPROVED) {
      result = false
    }
    if (!result) {
      break
    }
  }
  return result
})
</script>

<style>
.card-margin {
  margin-bottom: 6px;
}

.form-spacing {
  padding-left: 2px;
  margin-top: -20px;
}
</style>
